import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useNavigate } from "react-router-dom";
import { landingAssetsApi } from "../../api/landingAssets";
import { settingsApi } from "../../api/settings";
import PendingAssetField from "../../components/dashboard/landingEditor/PendingAssetField";
import { useAuth } from "../../context/AuthContext";
import { useProjects } from "../../context/ProjectsContext";
import { assetValueFromResponse } from "../../utils/landingAssets";
import { isSuperAdmin } from "../../utils/roles";
import {
  activeModuleLabels,
  BILLING_PLAN_LABELS,
  modulesForPlan,
  normalizeProjectModules,
  normalizeProjectPlanCode,
} from "../../utils/projectModules";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };
const emptyBranding = { logo: null, compactLogo: null, favicon: null };

function emptyForm(email = "") {
  return {
    name: "",
    slug: "",
    ownerEmail: email,
    plan: "catalogo",
    domains: "",
  };
}

function makeSlug(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

async function uploadInitialBranding(projectId, branding, businessName) {
  const uploads = [
    {
      file: branding.logo,
      field: "logoUrl",
      sectionKey: "branding",
      assetKey: "logo",
    },
    {
      file: branding.compactLogo,
      field: "compactLogoUrl",
      sectionKey: "branding",
      assetKey: "compact-logo",
    },
    {
      file: branding.favicon,
      field: "faviconUrl",
      sectionKey: "branding",
      assetKey: "favicon",
    },
  ].filter((item) => item.file);

  if (!uploads.length) return;

  const uploadedPaths = [];
  const theme = {};

  try {
    for (const item of uploads) {
      const response = await landingAssetsApi.upload(projectId, item.file, {
        pageKey: "home",
        sectionKey: item.sectionKey,
        assetKey: item.assetKey,
      });
      const value = assetValueFromResponse(response);
      if (!value) throw new Error("El backend no devolvió la ruta del archivo");
      uploadedPaths.push(value);
      theme[item.field] = value;
    }

    await settingsApi.update(projectId, {
      business: { name: businessName },
      seo: { title: businessName },
      theme,
    });
  } catch (error) {
    await Promise.all(
      uploadedPaths.map((path) => landingAssetsApi.remove(projectId, path).catch(() => null))
    );
    throw error;
  }
}

export default function ProjectsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const superAdmin = isSuperAdmin(user);
  const { projects, activeProject, setActiveProject, createProject, loadProjects, loading, error } = useProjects();
  const [form, setForm] = React.useState(() => emptyForm(user?.email || ""));
  const [branding, setBranding] = React.useState(emptyBranding);
  const [localError, setLocalError] = React.useState("");
  const [creating, setCreating] = React.useState(false);
  const [createStatus, setCreateStatus] = React.useState("");

  React.useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  React.useEffect(() => {
    setForm((previous) => ({
      ...previous,
      ownerEmail: previous.ownerEmail || user?.email || "",
    }));
  }, [user]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((previous) => {
      const next = { ...previous, [name]: value };
      if (name === "name") next.slug = makeSlug(value);
      return next;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    let project = null;

    try {
      setCreating(true);
      setCreateStatus("Creando proyecto y base del cliente...");
      setLocalError("");

      project = await createProject({
        name: form.name,
        slug: form.slug,
        ownerEmail: form.ownerEmail,
        plan: form.plan,
        domains: form.domains,
        modules: modulesForPlan(form.plan),
      });

      const hasBranding = Boolean(branding.logo || branding.compactLogo || branding.favicon);
      if (hasBranding && project?.slug) {
        setCreateStatus("Subiendo logos y favicon al backend...");
        await uploadInitialBranding(project.slug, branding, form.name);
      }

      const completedPlan = form.plan;
      setForm(emptyForm(user?.email || ""));
      setBranding(emptyBranding);
      setCreateStatus("");

      if (project?.slug) {
        const firstPath = hasBranding || completedPlan === "landing" ? "pagina" : "productos";
        navigate(`/dashboard/proyectos/${project.slug}/${firstPath}`);
      }
    } catch (err) {
      if (project?.slug) {
        setLocalError(
          `El proyecto ${project.name || project.slug} fue creado, pero no se pudo completar la identidad visual: ${err.message || "error desconocido"}. Podés reintentar desde Landing > Ajustes > Recursos visuales.`
        );
        await loadProjects();
      } else {
        setLocalError(err.message || "No se pudo crear el proyecto");
      }
    } finally {
      setCreating(false);
      setCreateStatus("");
    }
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h3" sx={{ fontWeight: 950, color: BRAND.dark }}>
          Proyectos
        </Typography>
        <Typography sx={{ color: "rgba(25,36,47,0.65)", mt: 0.5 }}>
          {superAdmin
            ? "Acá das de alta clientes. Cada proyecto apunta a una DB y un espacio de archivos propios."
            : "Estos son los proyectos/páginas que WebProfit te habilitó."}
        </Typography>
      </Box>

      {(error || localError) && <Alert severity="error" onClose={() => setLocalError("")}>{localError || error}</Alert>}

      <Grid container spacing={3}>
        {superAdmin && (
          <Grid item xs={12} md={5}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)" }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
                <Box sx={{ width: 44, height: 44, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,0.1)", color: BRAND.orange }}>
                  <AddRoundedIcon />
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>
                    Nuevo cliente
                  </Typography>
                  <Typography variant="body2" sx={{ color: "rgba(25,36,47,0.62)" }}>
                    Proyecto, dominio e identidad inicial en un solo flujo.
                  </Typography>
                </Box>
              </Stack>

              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={2}>
                  <TextField label="Nombre del negocio" name="name" value={form.name} onChange={handleChange} required fullWidth />
                  <TextField label="Slug" name="slug" value={form.slug} onChange={handleChange} required fullWidth helperText="Ej: repuestos-rr → DB: wp_repuestos_rr" />
                  <TextField label="Email owner" name="ownerEmail" type="email" value={form.ownerEmail} onChange={handleChange} required fullWidth />
                  <TextField select label="Plan" name="plan" value={form.plan} onChange={handleChange} fullWidth>
                    <MenuItem value="landing">Landing</MenuItem>
                    <MenuItem value="catalogo">Catálogo</MenuItem>
                    <MenuItem value="pedidos">Pedidos</MenuItem>
                    <MenuItem value="pro">Pro</MenuItem>
                    <MenuItem value="plus">Plus</MenuItem>
                  </TextField>
                  <TextField
                    label="Dominio público"
                    name="domains"
                    value={form.domains}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    minRows={2}
                    helperText="Opcional. Uno por línea o separados por coma. Podés pegar https://cliente.com y el backend guardará sólo el hostname."
                  />

                  <Divider />
                  <Box>
                    <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Identidad inicial</Typography>
                    <Typography variant="body2" sx={{ color: "rgba(25,36,47,.60)", mt: 0.25 }}>
                      Opcional. Después se puede reemplazar desde el editor visual sin tocar código.
                    </Typography>
                  </Box>
                  <PendingAssetField
                    label="Logo principal"
                    file={branding.logo}
                    disabled={creating}
                    helperText="SVG, PNG, WebP o JPG. Se guarda en el espacio del cliente."
                    onChange={(file) => setBranding((previous) => ({ ...previous, logo: file }))}
                  />
                  <PendingAssetField
                    label="Logo compacto"
                    file={branding.compactLogo}
                    disabled={creating}
                    helperText="Versión cuadrada o reducida para navbar móvil."
                    onChange={(file) => setBranding((previous) => ({ ...previous, compactLogo: file }))}
                  />
                  <PendingAssetField
                    label="Favicon"
                    file={branding.favicon}
                    disabled={creating}
                    accept="image/svg+xml,image/png,image/webp,image/x-icon,.svg,.ico"
                    helperText="Preferentemente cuadrado. SVG, PNG, WebP o ICO."
                    onChange={(file) => setBranding((previous) => ({ ...previous, favicon: file }))}
                  />

                  {createStatus ? <Alert severity="info">{createStatus}</Alert> : null}
                  <Button type="submit" disabled={creating} variant="contained" size="large" sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900 }}>
                    {creating ? "Preparando cliente..." : "Crear proyecto"}
                  </Button>
                </Stack>
              </Box>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12} md={superAdmin ? 7 : 12}>
          <Stack spacing={2}>
            {loading && <Alert severity="info">Cargando proyectos...</Alert>}
            {!loading && projects.length === 0 && (
              <Alert severity="info">Todavía no hay proyectos. Creá el primero para comenzar.</Alert>
            )}
            {projects.map((projectItem) => {
              const isActive = activeProject?.slug === projectItem.slug;
              const modules = normalizeProjectModules(projectItem.modules);
              const moduleLabels = activeModuleLabels(projectItem);
              return (
                <Card key={projectItem.id || projectItem.slug} elevation={0} sx={{ borderRadius: 4, border: `1px solid ${isActive ? BRAND.orange : "rgba(25,36,47,0.08)"}` }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={2}>
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>
                            {projectItem.name}
                          </Typography>
                          {isActive && <Chip label="Activo" size="small" sx={{ bgcolor: "rgba(243,90,58,0.12)", color: BRAND.orange, fontWeight: 900 }} />}
                        </Stack>
                        <Typography sx={{ color: "rgba(25,36,47,0.62)", mt: 0.75 }}>
                          /{projectItem.slug} · DB: {projectItem.dbName} · Plan: {BILLING_PLAN_LABELS[normalizeProjectPlanCode(projectItem.billing?.planCode || projectItem.plan)]}
                        </Typography>
                        {projectItem.domains?.length ? (
                          <Typography variant="body2" sx={{ color: "rgba(25,36,47,.58)", mt: 0.45 }}>
                            Dominio: {projectItem.domains.join(", ")}
                          </Typography>
                        ) : null}
                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                          <Chip size="small" label={`Billing: ${projectItem.billing?.status || "trial"}`} color={["active", "trial", "exempt"].includes(projectItem.billing?.status || "trial") ? "success" : "warning"} />
                          <Chip size="small" label={`Página: ${projectItem.status || "active"}`} variant="outlined" />
                        </Stack>
                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
                          {moduleLabels.length > 0 ? moduleLabels.map((label) => (
                            <Chip key={label} size="small" label={label} variant="outlined" />
                          )) : <Chip size="small" label="Sin módulos" />}
                        </Stack>
                      </Box>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button variant={isActive ? "contained" : "outlined"} onClick={() => setActiveProject(projectItem)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, bgcolor: isActive ? BRAND.orange : "transparent", borderColor: "rgba(25,36,47,0.18)" }}>
                          Usar
                        </Button>
                        {superAdmin && (
                          <Button onClick={() => navigate(`/dashboard/proyectos/${projectItem.slug}/configuracion/general`)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, color: BRAND.dark }}>
                            Administrar
                          </Button>
                        )}
                        <Button onClick={() => navigate(`/dashboard/proyectos/${projectItem.slug}/configuracion/suscripcion`)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, color: BRAND.orange }}>
                          Plan
                        </Button>
                        {modules.landing && (
                          <Button endIcon={<ArrowForwardRoundedIcon />} onClick={() => navigate(`/dashboard/proyectos/${projectItem.slug}/pagina`)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, color: BRAND.dark }}>
                            Landing
                          </Button>
                        )}
                        {modules.products && (
                          <Button endIcon={<ArrowForwardRoundedIcon />} onClick={() => navigate(`/dashboard/proyectos/${projectItem.slug}/productos`)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, color: BRAND.dark }}>
                            Productos
                          </Button>
                        )}
                        {modules.orders && (
                          <Button endIcon={<ArrowForwardRoundedIcon />} onClick={() => navigate(`/dashboard/proyectos/${projectItem.slug}/pedidos`)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, color: BRAND.dark }}>
                            Pedidos
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
}
